/* eslint-disable eqeqeq */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Container,
  Divider,
  DropdownItemProps,
  Form,
  FormGroup,
  Grid,
  Icon,
  Label,
  Message,
} from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import { Formik, FormikErrors, FormikHelpers } from "formik";
import * as Yup from "yup";
import { observer } from "mobx-react-lite";
import MyIconDecimalInput from "../../../../app/common/form/MyIconDecimalInput";
import MyTextInput from "../../../../app/common/form/MyTextInput";
import {
  round2Decimals,
  round3Decimals,
  getNegative,
  formatDecimal,
  isGuid,
} from "../../../../app/common/util/functions";
import PageHeader from "../../../../app/layout/PageHeader";
import { Application, TicketItemType } from "../../../../app/models/enums";
import {
  TicketItemFormValues,
  TicketItem,
} from "../../../../app/models/ticket";
import { useStore } from "../../../../app/stores/store";
import PartSearchResult from "./PartSearchResult";
import InventoryStockLocationSelect from "../../../../app/common/form/InventoryStockLocationSelect";
import {
  PartInventoryFormValues,
  SavePurchaseFormValues,
} from "../../../../app/models/inventory";
import MyCheckbox from "../../../../app/common/form/MyCheckbox";
import InventoryStockVendorSelect from "../../../../app/common/form/InventoryStockVendorSelect";
import { VendorsFormValues } from "../../../../app/models/vendor";

interface Props {
  isAdd: boolean;
  part?:
  | TicketItemFormValues
  | PartInventoryFormValues
  | SavePurchaseFormValues;
  origUnitPrice?: number | undefined;
  addItem: (
    values:
      | TicketItemFormValues
      | PartInventoryFormValues
      | SavePurchaseFormValues,
    type: TicketItemType
  ) => void;
  autoCapitalizeFirstLetter: boolean;
  vendorDropdownList: DropdownItemProps[];
  forInventory?: boolean;
  forPurchases?: boolean;
  useInventory?: boolean;
  quantityInStock?: number | undefined;
  excludeInventoryParts?: boolean | undefined;
}

export default observer(function PartItemForm({
  isAdd,
  part,
  origUnitPrice,
  addItem,
  autoCapitalizeFirstLetter,
  vendorDropdownList,
  forInventory,
  forPurchases,
  quantityInStock,
  excludeInventoryParts
}: Props) {
  const { t } = useTranslation([
    "common",
    "translation",
    "tickets",
    "inventory",
  ]);
  const {
    modalStore,
    vendorStore,
    partsStore,
    userStore,
    inventoryIndexStore,
    inventorySettingsStore,
  } = useStore();
  const { inventorySettings } = inventorySettingsStore;
  const { selectedSettingsVendor, loadVendors, getVendor, loadSettingVendors } = vendorStore;
  const {
    setInventoryStockLocationList,
    inventoryStockLocationList,
  } = inventoryIndexStore;

  const { resetSearchPartRegistry, isPartAlreadyInInventory } = partsStore;

  const { checkApplication } = userStore;

  const isAE = useMemo(
    () => checkApplication(Application.AutomotiveExpert),
    []
  );
  const useInventorySettings = useMemo(
    () => isAE && forInventory && inventorySettings?.isActive,
    [inventorySettings]
  );
  const useCoreTracking = useMemo(
    () => isAE && inventorySettings?.enableCoreTracking,
    [inventorySettings]
  );
  const useLowInventoryReminder = useMemo(
    () => isAE && inventorySettings?.enableNotifyLowInventory,
    [inventorySettings]
  );

  const [searchedPartCode, setSearchedPartCode] = useState<string | null>();

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  useEffect(() => {
    loadSettingVendors();
  }, [loadSettingVendors]);

  useEffect(() => {
    if (part) {
      let rate = calculateRateWithUnitPrice(
        part.unitPrice ?? 0,
        part.partMarkup ?? 0
      );
      calculateProfit(rate, part.unitPrice ?? 0, part.quantity);
    }
  }, []);

  const [partItem] = useState<
    TicketItemFormValues | PartInventoryFormValues | SavePurchaseFormValues
  >(
    forPurchases
      ? new SavePurchaseFormValues(part)
      : forInventory
        ? new PartInventoryFormValues(part)
        : new TicketItemFormValues(part)
  );
  const [profit, setProfit] = useState<number>(0);

  const validationSchema = Yup.object({});

  async function handleFormSubmit(
    values:
      | TicketItem
      | TicketItemFormValues
      | PartInventoryFormValues
      | SavePurchaseFormValues,
    formikHelpers: FormikHelpers<TicketItem | TicketItemFormValues | PartInventoryFormValues | SavePurchaseFormValues>
  ) {
    if (
      searchedPartCode &&
      values.partId &&
      values.partCode != searchedPartCode
    ) {
      // If the user pulled up a part and changed the code, they are making a new part
      values.partId = undefined;
    }
    
    if (forInventory && isAdd && (await isPartAlreadyInInventory(values.partCode!))) {
      return formikHelpers.setFieldError("partCode", t("addExistPartToInventory", { ns: "errors" }));
    }

    addItem(values, TicketItemType.Parts);
    modalStore.closeModal();
  }

  function calculateFieldsFromVendor(
    vendorId: string | null,
    quantity: number,
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean | undefined
    ) => Promise<void | FormikErrors<TicketItem>>,
    partMarkup?: number,
    unitPrice?: number,
    rate?: number
  ) {

    let markup = calculateMarkupFromVendor(
      vendorId,
      setFieldValue,
      partMarkup
    );

    if (quantity && (unitPrice || rate) && markup) {
      let myRate: number = rate ?? 0;
      let myUnitPrice: number = unitPrice ?? 0;
      if (unitPrice) {
        myRate = calculateRateWithUnitPrice(
          myUnitPrice,
          markup,
          setFieldValue
        );
        calculateSubTotal(quantity, myRate, setFieldValue);
      } else if (rate) {
        myUnitPrice = calculateUnitPrice(rate, markup, setFieldValue);
        calculateSubTotal(quantity, myRate, setFieldValue);
      }
      calculateProfit(myRate, myUnitPrice, quantity);
    }
  }

  function calculateMarkupFromVendor(
    vendorId: string | null,
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean | undefined
    ) => Promise<void | FormikErrors<TicketItem>>,
    partMarkup?: number
  ) {
    let vendor = vendorId ? getVendor(vendorId) : null;

    let markup: number = partMarkup ?? 0.0;

    const defaultMarkup = vendor
      ? vendor?.defaultMarkup
      : selectedSettingsVendor.defaultMarkup;

    if (defaultMarkup !== null) {
      markup = round2Decimals(defaultMarkup);
      setFieldValue("partMarkup", round2Decimals(markup).toString());
    }
    return markup;
  }

  function calculateMarkup(
    unitPrice: number,
    rate: number,
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean | undefined
    ) => Promise<void | FormikErrors<TicketItem>>
  ) {
    let markup: number = 0.0;
    if (unitPrice && unitPrice != 0) {
      if (unitPrice - rate != 0) {
        markup = ((rate - unitPrice) / unitPrice) * 100;
      }
    }
    setFieldValue("partMarkup", round2Decimals(markup).toString());
    return markup;
  }

  function calculateRateWithUnitPrice(
    unitPrice: number,
    markup: number,
    setFieldValue?: (
      field: string,
      value: any,
      shouldValidate?: boolean | undefined
    ) => Promise<void | FormikErrors<TicketItem>>
  ) {
    let rate = unitPrice * (1 + markup * 0.01);

    if (setFieldValue) {
      setFieldValue("rate", round3Decimals(rate));
    }

    return rate;
  }

  function calculateRateWithSubTotal(
    quantity: number,
    subTotal: number,
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean | undefined
    ) => Promise<void | FormikErrors<TicketItem>>
  ) {
    let rate: number = 0.0;
    if (subTotal && quantity) {
      rate = Number(subTotal) / Number(quantity);
      setFieldValue("rate", round3Decimals(rate));
    }
    return rate;
  }

  function calculateUnitPrice(
    rate: number,
    markup: number,
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean | undefined
    ) => Promise<void | FormikErrors<TicketItem>>
  ) {
    let unitPrice = rate / (1 + markup * 0.01);

    setFieldValue("unitPrice", round2Decimals(unitPrice));
    return unitPrice;
  }

  function calculateSubTotal(
    quantity: number,
    rate: number,
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean | undefined
    ) => Promise<void | FormikErrors<TicketItem>>
  ) {
    let subtotal = Number(quantity) * Number(rate);
    setFieldValue("subTotal", round2Decimals(subtotal));
    return subtotal;
  }

  function calculateProfit(rate: number, unitPrice: number, quantity: number) {
    let profit = Number(rate ?? 0) - Number(unitPrice ?? 0);
    setProfit(round2Decimals(profit));
    return profit;
  }

  function search(
    values: TicketItemFormValues | TicketItem,
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean | undefined
    ) => Promise<void | FormikErrors<TicketItem>>,
    searchPartCode?: string,
    searchPartName?: string
  ) {
    resetSearchPartRegistry();
    modalStore.openWidestModal(
      <PartSearchResult
        partCode={searchPartCode}
        description={searchPartName}
        addItem={(data) => {
          values.partId = data.id;
          values.partCode = data.partCode;
          values.description = data.name;

          values.coreTrackingEnabled =
            data.coreTrackingEnabled != false
              ? data.coreTrackingEnabled
              : data.coreTrackingId != undefined;
          if (values.coreTrackingEnabled) {
            values.coreTrackingId = data.coreTrackingId;
            values.coreValue = data.coreValue;
            values.returned = data.returned;
            values.refunded = data.refunded;
          } else {
            values.coreTrackingId = undefined;
            values.coreValue = undefined;
            values.returned = undefined;
            values.refunded = undefined;
          }

          values.inventoryStockId = data.inventoryStockId;

          if (data.inInventory && forInventory) {
            values.quantity = round2Decimals(
              data.inventoryStockId && isAdd ? data.quantityInStock ?? 1 : 1
            );
            values.reOrderLevel =
              data.reOrderLevel ?? inventorySettings?.lowInventoryDefault;
            values.locationId = data.locationId;
          } else {
            values.quantity =
              data.quantity && !isAdd ? round2Decimals(data.quantity ?? 1) : 1;
            values.reOrderLevel = inventorySettings?.lowInventoryDefault;
            values.locationId = undefined;
          }

          //values.rate = round3Decimals(unitPrice ?? 0.0);
          if (values.vendorId && !isGuid(values.vendorId))
          {
            values.vendorId = undefined;
          }
          if (!values.vendorId) {
            values.vendorId = data.vendorId;
            values.partMarkup = data.markup;
          }
          values.unitPrice = data.unitPrice;

          values.rate = calculateRateWithUnitPrice(
            values.unitPrice ?? 0,
            values.partMarkup ?? 0,
            setFieldValue
          );
          /*
          unitPrice = round2Decimals(
            calculateUnitPrice(
              unitPrice ?? 0,
              values.partMarkup ?? 0,
              setFieldValue
            )
          );*/

          values.subTotal = round2Decimals(
            calculateSubTotal(values.quantity ?? 1, values.rate, setFieldValue)
          );

          //values.unitPrice = unitPrice;
          calculateProfit(
            values.rate,
            values.unitPrice ?? 0.0,
            values.quantity
          );
          values.inInventory = data.inInventory;
          modalStore.openModal(
            <PartItemForm
              addItem={addItem}
              isAdd={isAdd}
              part={values}
              origUnitPrice={values.unitPrice}
              autoCapitalizeFirstLetter={autoCapitalizeFirstLetter}
              vendorDropdownList={vendorDropdownList}
              quantityInStock={isAE ? data.quantityInStock : undefined}
              forInventory={forInventory}
              forPurchases={forPurchases}
              excludeInventoryParts={excludeInventoryParts}
            />
          );
        }}
        cancelSearch={() => {
          modalStore.openModal(
            <PartItemForm
              addItem={addItem}
              isAdd={isAdd}
              part={values}
              autoCapitalizeFirstLetter={autoCapitalizeFirstLetter}
              vendorDropdownList={vendorDropdownList}
              forInventory={forInventory}
              forPurchases={forPurchases}
              excludeInventoryParts={excludeInventoryParts}
            />
          );
        }}
        isAE={isAE}
        inventoryEnabled={inventorySettings?.isActive ?? false}
        coreTrackingEnabled={inventorySettings?.enableCoreTracking ?? false}
        excludeInventoryParts={excludeInventoryParts}
      />
    );
  }

  function getHeader() {
    if (isAdd && forPurchases)
      return t("labels.addPartPurchase", {
        ns: "inventory",
      });
    else if (isAdd && !forInventory)
      return t("pops.add_prt_itm", {
        ns: "tickets",
      });
    else if (isAdd && forInventory)
      return t("labels.addToPartToStock", {
        ns: "inventory",
      });
    else if (!isAdd && forInventory)
      return t("labels.editPartInStock", {
        ns: "inventory",
      });
    else
      return t("pops.edit_prt_itm", {
        ns: "tickets",
      });
  }

  const initialValues = useMemo(() => {
    if (useInventorySettings)
      return {
        ...partItem,
        unitPrice: origUnitPrice ? origUnitPrice : partItem.unitPrice,
        partMarkup: (partItem.partMarkup ?? 0) > 0 ? partItem.partMarkup : selectedSettingsVendor.defaultMarkup,
        subTotal: partItem.rate && partItem.quantity ? (partItem.rate * partItem.quantity) : undefined,
        coreTrackingEnabled:
          partItem.coreTrackingEnabled != undefined
            ? partItem.coreTrackingEnabled
            : useCoreTracking
              ? inventorySettings?.enableCoreTracking
              : false,
        lowInventoryReminderEnabled:
          (partItem as PartInventoryFormValues).lowInventoryReminderEnabled !=
            undefined
            ? (partItem as PartInventoryFormValues).lowInventoryReminderEnabled
            : inventorySettings?.enableNotifyLowInventory ?? false,
      };

    return {
      ...partItem,
      coreTrackingEnabled:
        partItem.coreTrackingEnabled != undefined
          ? partItem.coreTrackingEnabled
          : useCoreTracking
            ? inventorySettings?.enableCoreTracking
            : false,
      unitPrice: origUnitPrice ? origUnitPrice : partItem.unitPrice,
      partMarkup: (partItem.partMarkup ?? 0) > 0 ? partItem.partMarkup : selectedSettingsVendor.defaultMarkup,
    };
  }, [inventorySettings, selectedSettingsVendor]);

  const usingInventory = useMemo(() => partItem.inInventory || partItem.inventoryStockId, [partItem]);

  return (
    <>
      <PageHeader
        type={"h3"}
        header={getHeader()}
        divider={true}
        addTitle={false}
      />
      {usingInventory && (
        <Message warning>
          {t("usingInventory", { ns: "inventory" })}
          <br />
          <a
            href={`/inventoryIndex/partDetails/${partItem.partId}`}
            target="_blank"
            rel="noreferrer"
          >
            {t("checkInventory", { ns: "inventory" })}
          </a>
        </Message>
      )}
      <Formik
        validationSchema={validationSchema}
        initialValues={
          forPurchases
            ? (initialValues as SavePurchaseFormValues)
            : forInventory
              ? (initialValues as PartInventoryFormValues)
              : initialValues
        }
        onSubmit={(values: TicketItem, formikHelpers: FormikHelpers<TicketItem>) => handleFormSubmit(values, formikHelpers)}
      >
        {({ handleSubmit, values, setFieldValue, setFieldError, errors }) => (
          <Form
            className="ui form error"
            autoComplete="on"
            onSubmit={handleSubmit}
          >
            <Grid stackable>
              <Grid.Row columns={2} className="crampedModal">
                <Grid.Column verticalAlign="bottom">
                  <Container className="search-input">
                    <MyTextInput
                      placeholder={t("estimate.part_no", { ns: "tickets" })}
                      label={t("estimate.part_no", { ns: "tickets" }) + ":"}
                      className="full-width"
                      name="partCode"
                      autoCapitalizeFirstLetter={autoCapitalizeFirstLetter}
                      maxLength={50}
                      autoFocus={true}
                      onBlur={async (v) => {
                        if (part && part.partId && part.partCode != v) {
                          // If the part code changed, the user wants to make a new part from the existing part
                          setSearchedPartCode(part.partCode);
                        }

                        if (forInventory && isAdd && (await isPartAlreadyInInventory(values.partCode!))) {
                          return setFieldError("partCode", t("addExistPartToInventory", { ns: "errors" }));
                        }
                      }}
                    />
                    <Button
                      icon="search"
                      attached="right"
                      type="button"
                      className="searchButton"
                      onClick={() => {
                        if (values.partCode)
                          search(values, setFieldValue, values.partCode);
                      }}
                    />
                  </Container>
                </Grid.Column>
                <Grid.Column>
                  <Container className="search-input">
                    <MyTextInput
                      placeholder={t("pops.part_desc", { ns: "tickets" })}
                      label={t("pops.part_desc", { ns: "tickets" }) + ":"}
                      className="full-width"
                      name="description"
                      autoCapitalizeFirstLetter={autoCapitalizeFirstLetter}
                      maxLength={1000}
                    />
                    <Button
                      icon="search"
                      attached="right"
                      type="button"
                      className="searchButton"
                      onClick={() => {
                        if (values.description)
                          search(
                            values,
                            setFieldValue,
                            undefined,
                            values.description
                          );
                      }}
                    />
                  </Container>
                </Grid.Column>
              </Grid.Row>
              {selectedSettingsVendor.enablePartMarkup && (
                <>
                  <Grid.Row columns={2} className="crampedModal">
                    <Grid.Column width={11}>
                      <InventoryStockVendorSelect
                        options={vendorDropdownList}
                        label={t("pops.vendor", { ns: "tickets" }) + ":"}
                        placeholder={t("pops.vendor", { ns: "tickets" })}
                        name="vendorId"
                        disabled={forPurchases}
                        onChange={(d) => {
                          calculateFieldsFromVendor(
                            d,
                            values.quantity,
                            setFieldValue,
                            values.partMarkup,
                            values.unitPrice,
                            values.rate
                          );
                        }}
                        onCloseModal={(v?: VendorsFormValues) => {
                          // getting a new vendor list was causing race condition, but we know what the values are so we can just append
                          if (
                            v &&
                            !vendorDropdownList.find((x) => x.value === v.id)
                          ) {
                            vendorDropdownList.push({
                              key: v.id,
                              text: v.name,
                              value: v.id,
                            });
                            values.vendorId = v.id;
                            values.partMarkup = v.defaultMarkup;
                          }
                          modalStore.openModal(
                            <PartItemForm
                              addItem={addItem}
                              isAdd={isAdd}
                              part={values}
                              autoCapitalizeFirstLetter={
                                autoCapitalizeFirstLetter
                              }
                              vendorDropdownList={vendorDropdownList}
                              forInventory={forInventory}
                            />
                          );
                        }}
                      />
                    </Grid.Column>
                    <Grid.Column width={5}>
                      <MyIconDecimalInput
                        icon="percent"
                        placeholder="0.00"
                        label={t("pops.mark_up", { ns: "tickets" }) + ":"}
                        name="partMarkup"
                        onChange={(d) => {
                          if ((d && values.quantity, values.unitPrice)) {
                            let rate = calculateRateWithUnitPrice(
                              values.unitPrice,
                              d,
                              setFieldValue
                            );
                            calculateSubTotal(
                              values.quantity,
                              rate,
                              setFieldValue
                            );
                            calculateProfit(
                              rate,
                              values.unitPrice,
                              values.quantity
                            );
                          }
                        }}
                        onBlur={(data) => {
                          if ((data && values.quantity, values.unitPrice)) {
                            let rate = calculateRateWithUnitPrice(
                              values.unitPrice,
                              data,
                              setFieldValue
                            );
                            calculateSubTotal(
                              values.quantity,
                              rate,
                              setFieldValue
                            );
                            calculateProfit(
                              rate,
                              values.unitPrice,
                              values.quantity
                            );
                          }
                        }}
                        maxLength={500}
                      />
                    </Grid.Column>
                  </Grid.Row>
                  <Grid.Row columns={2} className="crampedModal">
                    <Grid.Column>
                      <MyIconDecimalInput
                        icon="dollar sign"
                        iconPosition="left"
                        placeholder="0.00"
                        label={t("pops.cost", { ns: "tickets" }) + ":"}
                        name="unitPrice"
                        onChange={(d) => {
                          if (d && values.quantity && values.partMarkup) {
                            let rate = calculateRateWithUnitPrice(
                              d,
                              values.partMarkup,
                              setFieldValue
                            );
                            calculateSubTotal(
                              values.quantity,
                              rate,
                              setFieldValue
                            );
                            calculateProfit(rate, d, values.quantity);
                          }
                        }}
                        onBlur={(d) => {
                          //checked
                          if (d && values.quantity && values.partMarkup) {
                            let rate = calculateRateWithUnitPrice(
                              d,
                              values.partMarkup,
                              setFieldValue
                            );
                            calculateSubTotal(
                              values.quantity,
                              rate,
                              setFieldValue
                            );
                            calculateProfit(rate, d, values.quantity);
                          }
                        }}
                        maxLength={500}
                      />
                    </Grid.Column>
                    <Grid.Column>
                      <label className={"myLabel"}>
                        {t("pops.profit", { ns: "tickets" })}:
                      </label>
                      <br />
                      <Label
                        basic
                        simple
                        fluid
                        className="myLabel shortInput labelHeight"
                        style={{
                          color: getNegative(profit ?? 0),
                        }}
                      >
                        <Icon name="dollar" />
                        {formatDecimal(round2Decimals(profit ?? 0) ?? 0, 2)}
                      </Label>
                    </Grid.Column>
                  </Grid.Row>
                </>
              )}
              {checkApplication(Application.AutomotiveExpert) && (
                <>
                  {/* Conditionally show fields when adding to the inventory or updating from inventory related pages */}
                  {forInventory || forPurchases ? (
                    <>
                      <Grid.Row columns={1} className="crampedModal">
                        <Grid.Column>
                          <InventoryStockLocationSelect
                            options={inventoryStockLocationList}
                            setOptions={setInventoryStockLocationList}
                            label={
                              t("pops.shop_location", { ns: "tickets" }) + ":"
                            }
                            placeholder={t("pops.shop_location", {
                              ns: "tickets",
                            })}
                            name="locationId"
                            clearable={true}
                            onChange={(val) => {
                              const selectedLoc =
                                inventoryStockLocationList.find(
                                  (invStLoc) => invStLoc.value === val
                                );

                              if (selectedLoc) {
                                setFieldValue("location", selectedLoc.text);
                                setFieldValue("locationId", selectedLoc.value);
                              } else {
                                setFieldValue("location", val);
                              }
                            }}
                            closeAddLocation={async (
                              locationValues?: DropdownItemProps
                            ) => {
                              if (locationValues) {
                                setInventoryStockLocationList([
                                  ...inventoryStockLocationList,
                                  locationValues,
                                ]);
                                values.locationId =
                                  locationValues.value as string;
                              }

                              modalStore.openModal(
                                <PartItemForm
                                  addItem={addItem}
                                  isAdd={isAdd}
                                  part={values}
                                  autoCapitalizeFirstLetter={
                                    autoCapitalizeFirstLetter
                                  }
                                  vendorDropdownList={vendorDropdownList}
                                  forInventory={forInventory}
                                />
                              );
                            }}
                          />
                        </Grid.Column>
                      </Grid.Row>
                      {useLowInventoryReminder && (
                        <Grid.Row className="halfCrampedModal">
                          <Grid.Column>
                            <FormGroup>
                              <MyCheckbox
                                label={`${t(
                                  "inventory.lowInventoryNotifications",
                                  {
                                    ns: "settings",
                                  }
                                )}`}
                                name={"lowInventoryReminderEnabled"}
                                showHelp={true}
                                helpName="lowInventoryReminderEnabled"
                              />
                            </FormGroup>
                          </Grid.Column>
                        </Grid.Row>
                      )}
                      {(values as PartInventoryFormValues)["lowInventoryReminderEnabled"] && (
                        <Grid.Row columns={2} className="crampedModal">
                          <Grid.Column>
                            <MyIconDecimalInput
                              icon="box"
                              iconPosition="left"
                              placeholder="0.00"
                              label={`${t("pops.reorder_qty", {
                                ns: "tickets",
                              })}:`}
                              name="reOrderLevel"
                              maxLength={500}
                            />
                          </Grid.Column>
                        </Grid.Row>
                      )}
                      {useCoreTracking && (
                        <Grid.Row className="halfCrampedModal">
                          <Grid.Column>
                            <FormGroup>
                              <MyCheckbox
                                label={`${t("coreTracking", {
                                  ns: "settings",
                                })}`}
                                name={"coreTrackingEnabled"}
                                showHelp={true}
                                helpName="coreTrackingEnabled"
                              />
                            </FormGroup>
                          </Grid.Column>
                        </Grid.Row>
                      )}
                    </>
                  ) : (
                    <>
                      {useCoreTracking && (
                        <MyCheckbox
                          label={`${t("Core Tracking", {
                            ns: "settings",
                          })}`}
                          name={"coreTrackingEnabled"}
                          showHelp={true}
                          helpName="coreTrackingEnabled"
                        />
                      )}
                    </>
                  )}
                  {(values as any)["coreTrackingEnabled"] && (
                    <>
                      {/*<AccordionContainer classNames="two column row" title={t("labels.coreTrackingAccordion", { ns: "inventory" })}>*/}
                      <Grid.Row columns={1} className="crampedModal">
                        <Grid.Column>
                          <MyIconDecimalInput
                            icon="dollar"
                            iconPosition="left"
                            placeholder="0.00"
                            label={`${t("labels.coreValue", {
                              ns: "inventory",
                            })}:`}
                            name="coreValue"
                            maxLength={500}
                          />
                        </Grid.Column>
                      </Grid.Row>
                      <Divider />
                      {/*</AccordionContainer>*/}
                    </>
                  )}
                </>
              )}
              {!forPurchases && (
              <Grid.Row className="crampedModal">
                <Grid.Column>
                  <MyIconDecimalInput
                    icon="boxes"
                    iconPosition="left"
                    placeholder="0.00"
                    label={t("pops.qty", { ns: "tickets" }) + ":"}
                    name="quantity"
                    onChange={(d) => {
                      if (d && values.rate) {
                        calculateSubTotal(d, values.rate, setFieldValue);
                        if (
                          selectedSettingsVendor.enablePartMarkup &&
                          values.unitPrice
                        ) {
                          calculateProfit(values.rate, values.unitPrice, d);
                        }
                      }
                    }}
                    onBlur={(d) => {
                      //checked
                      if (d && values.rate) {
                        calculateSubTotal(d, values.rate, setFieldValue);
                        if (
                          selectedSettingsVendor.enablePartMarkup &&
                          values.unitPrice
                        ) {
                          calculateProfit(values.rate, values.unitPrice, d);
                        }
                      }
                    }}
                    maxLength={500}
                  />
                  {usingInventory &&
                    values.quantity > (quantityInStock ?? 0) && (
                      <Message error>
                        {t("lowStockShort", {
                          ns: "inventory",
                          q: { count: quantityInStock },
                        })}
                      </Message>
                    )}
                </Grid.Column>
              </Grid.Row>
              )}
              <Grid.Row className="crampedModal">
                <Grid.Column>
                  <MyIconDecimalInput
                    icon="dollar sign"
                    iconPosition="left"
                    placeholder="0.000"
                    label={t("pops.price_each", { ns: "tickets" }) + ":"}
                    name="rate"
                    onChange={(d) => {
                      if (d && values.quantity) {
                        calculateSubTotal(
                          values.quantity,
                          Number(d),
                          setFieldValue
                        );
                        calculateProfit(
                          Number(d),
                          values.unitPrice ?? 0,
                          values.quantity
                        );
                        calculateMarkup(
                          values.unitPrice ?? 0,
                          Number(d),
                          setFieldValue
                        );
                      }
                    }}
                    onBlur={(d) => {
                      //checked
                      if (d && values.quantity) {
                        calculateSubTotal(
                          values.quantity,
                          Number(d),
                          setFieldValue
                        );
                        calculateProfit(
                          Number(d),
                          values.unitPrice ?? 0,
                          values.quantity
                        );
                        calculateMarkup(
                          values.unitPrice ?? 0,
                          Number(d),
                          setFieldValue
                        );
                      }
                    }}
                    significantDigits={3}
                    maxLength={500}
                  />
                </Grid.Column>
              </Grid.Row>
              {!forPurchases && (
              <Grid.Row className="crampedModal">
                <Grid.Column>
                  <MyIconDecimalInput
                    icon="dollar sign"
                    iconPosition="left"
                    placeholder="0.00"
                    label={t("value", { ns: "reports" }) + ":"}
                    name="subTotal"
                    onChange={(d) => {
                      if (d && values.quantity) {
                        let rate = calculateRateWithSubTotal(
                          values.quantity,
                          d,
                          setFieldValue
                        );
                        if (
                          values.unitPrice &&
                          selectedSettingsVendor.enablePartMarkup
                        ) {
                          calculateProfit(
                            rate,
                            values.unitPrice,
                            values.quantity
                          );
                          calculateMarkup(
                            values.unitPrice,
                            rate,
                            setFieldValue
                          );
                        }
                      }
                    }}
                    onBlur={(d) => {
                      if (d && values.quantity) {
                        let rate = calculateRateWithSubTotal(
                          values.quantity,
                          d,
                          setFieldValue
                        );
                        if (
                          values.unitPrice &&
                          selectedSettingsVendor.enablePartMarkup
                        ) {
                          calculateProfit(
                            rate,
                            values.unitPrice,
                            values.quantity
                          );
                          calculateMarkup(
                            values.unitPrice,
                            rate,
                            setFieldValue
                          );
                        }
                      }
                    }}
                    maxLength={500}
                  />
                </Grid.Column>
              </Grid.Row>
              )}
              {/* commenting out until later, per OGB */}
              {/* <Grid.Row className="crampedModal">
                {checkApplication(Application.AutomotiveExpert) && (
                  <AccordionContainer
                    title={t("labels.notes", { ns: "inventory" })}
                  >
                    <MyTextArea
                      label={""}
                      placeholder={t("placeholder.notes", { ns: "inventory" })}
                      name={"notes"}
                      rows={4}
                      maxLength={1000}
                    />
                  </AccordionContainer>
                )}
              </Grid.Row> */}
              <Divider />
              <Grid.Row className="crampedModal">
                <Grid.Column>
                  <Button color="green" type="submit" disabled={!!errors.partCode}>
                    <Icon name="check" />
                    {isAdd
                      ? t("pops.add_prt_itm", { ns: "tickets" })
                      : t("pops.save_prt_itm", { ns: "tickets" })}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      modalStore.closeModal();
                    }}
                  >
                    <Icon name="x" />
                    {t("cancel", { ns: "common" })}
                  </Button>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form>
        )}
      </Formik>
    </>
  );
});
